import type { SearchResult, SearchHistoryRecord } from "$lib/types";
import { SEARCH_PROGRESS, MATCH_STATUS } from "$lib/types";
import type { MatchStatus } from "$lib/types";
import { searchHistoryStore } from "$lib/stores";
import type { StoreUpdateFunction, ResultProcessFunction, StoreSaveFunction } from "./types";

export interface EventHandlerConfig {
  eventSource: EventSource;
  slug: string;
  updateStoreFn: StoreUpdateFunction;
  endSearchFn: (slug: string) => void;
  saveSearchToLocalStorage: StoreSaveFunction;
  processSearchResult: ResultProcessFunction;
  activeStreams: Record<string, EventSource>;
}

export class ExpandSearchHandler {
  /**
   * Sets up event handlers for the expand search process
   */
  static setupExpandSearchHandlers(config: EventHandlerConfig): EventSource {
    logger.log("Setting up EXPAND search handlers for:", config.slug);
    logger.log("Config:", config);

    const { eventSource } = config;

    eventSource.onopen = () => {
      logger.log("SSE connection opened for expand search:", config.slug);
    };

    eventSource.onmessage = (event) => this.handleExpandSearchMessage(event, config);

    eventSource.onerror = (error) => {
      this.handleExpandSearchError(error, config);
      eventSource.close();
    };

    eventSource.addEventListener("end", (event: Event) => {
      logger.log("[expand] End event received for expand search:", config.slug);
      logger.log("Event:", event);
      if (!(event instanceof MessageEvent)) {
        logger.log("End event received for expand search:", config.slug);
        this.handleExpandSearchEnd(config);
        return;
      }

      try {
        const jsonData = JSON.parse(event.data);
        logger.log("Expand search end event data:", jsonData);
        const findMore = jsonData.findMore ?? true;
        this.handleExpandSearchEnd(config, { findMore });
      } catch (error) {
        console.error("Error processing expand search end event:", error);
        this.handleExpandSearchEnd(config);
      } finally {
        this.cleanupEventSource(eventSource, config.activeStreams, config.slug);
        config.endSearchFn(config.slug);
      }
    });

    return config.eventSource;
  }

  /**
   * Updates the search history store
   */
  private static updateSearchHistoryStore(slug: string, update: SearchHistoryRecord): void {
    searchHistoryStore.updateSearch(slug, update);
  }

  /**
   * Handles messages during the expand search process
   */
  private static handleExpandSearchMessage(event: MessageEvent, config: EventHandlerConfig): void {
    try {
      const decodedData = atob(event.data);
      const jsonData = JSON.parse(decodedData);
      logger.log("Received expand search data:", jsonData);

      if (this.isValidSearchResult(jsonData)) {
        config.processSearchResult(config.slug, jsonData);

        config.updateStoreFn((states) => {
          const currentState = states[config.slug];
          if (!currentState) return states;

          this.updateSearchHistoryStore(config.slug, {
            matchesCount: this.getTotalMatchingResults(currentState.results || []),
          });

          return states;
        });
      }
    } catch (error) {
      console.error("Error processing expand search result:", error, "Raw event data:", event.data);
    }
  }

  /**
   * Handles the end of an expand search operation
   */
  private static handleExpandSearchEnd(
    config: EventHandlerConfig,
    options: { findMore: boolean } = { findMore: false },
  ): void {
    logger.log("Processing expand search end for:", config.slug);

    config.updateStoreFn((states) => {
      const currentState = states[config.slug];
      if (!currentState) return states;

      const updatedState = {
        ...currentState,
        findMore: options.findMore,
        totalResultsFound: this.getTotalMatchingResults(currentState.results || []),
        totalResultsGenerated: (currentState.results || []).length,
      };

      try {
        config.saveSearchToLocalStorage(updatedState);
        logger.log("Expand search state saved:", config.slug);
      } catch (error) {
        console.error("Failed to save expand search state:", error);
      }

      searchHistoryStore.updateSearch(config.slug, {
        matchesCount: updatedState.totalResultsFound,
        // updatedAt: new Date(),
      });

      return { ...states, [config.slug]: updatedState };
    });
  }

  /**
   * Handles expand search errors
   */
  private static handleExpandSearchError(error: Event, config: EventHandlerConfig): void {
    console.error("Error with expand search SSE:", error);

    config.updateStoreFn((states) => {
      const currentState = states[config.slug];
      if (!currentState) return states;

      const finalState = {
        ...currentState,
        progressStatus: SEARCH_PROGRESS.CANCELLED,
        error: "Error with expand search",
      };

      config.saveSearchToLocalStorage(finalState);

      return {
        ...states,
        [config.slug]: finalState,
      };
    });

    this.cleanupEventSource(config.eventSource, config.activeStreams, config.slug);
  }

  /**
   * Gets total matching results
   */
  private static getTotalMatchingResults(results: SearchResult[]): number {
    return results.filter((r) => r.match === MATCH_STATUS.DEFINITE_MATCH || r.match === MATCH_STATUS.PARTIAL_MATCH)
      .length;
  }

  /**
   * Type guard for search results
   */
  private static isValidSearchResult(result: unknown): result is SearchResult {
    if (!result || typeof result !== "object") return false;
    const r = result as Partial<SearchResult>;
    return !!(
      (r.firstName || r.lastName || r.companyName) &&
      r.match &&
      [
        MATCH_STATUS.DEFINITE_MATCH,
        MATCH_STATUS.PARTIAL_MATCH,
        MATCH_STATUS.NOT_MATCH,
        MATCH_STATUS.NEED_MORE_INFO,
      ].includes(r.match as MatchStatus)
    );
  }
  /**
   * Cleans up EventSource and related resources
   */
  private static cleanupEventSource(
    eventSource: EventSource,
    activeStreams: Record<string, EventSource>,
    slug: string,
  ): void {
    logger.log("Cleaning up expand search event source for:", slug);
    eventSource.close();
    delete activeStreams[slug];
  }
}
